import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Loader2, Package, Layers, ChevronDown, ChevronRight, ArrowRightLeft, Factory } from 'lucide-react';
import { productionAPI, bomAPI, branchAPI } from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';

const ProductionCreateForm = ({ onBack, prefillOC }) => {
  const [mode, setMode] = useState(prefillOC ? 'oc' : 'select'); // select, standalone, oc
  const [boms, setBoms] = useState([]);
  const [bomsTotal, setBomsTotal] = useState(0);
  const [bomsLoading, setBomsLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBom, setSelectedBom] = useState(null);
  const [bomSearch, setBomSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [expandedItems, setExpandedItems] = useState({});

  const [form, setForm] = useState({
    product: '', fg_item_id: '', quantity: 1, unit: 'PCS',
    bom_id: '', bom_number: '', oc_reference: prefillOC?.id || '',
    oc_number: prefillOC?.oc_number || '', branch: '',
    planned_start: new Date().toISOString().split('T')[0],
    planned_end: '', notes: '', materials: [],
  });

  // Branches (one-shot)
  useEffect(() => {
    branchAPI.getAll().catch(() => []).then(br => {
      setBranches(br.filter(x => x.type === 'Production'));
    });
  }, []);

  // BOMs — server-side search w/ debounce. Default: first 100 alphabetical.
  // When user types in the picker search, hits backend with ?search=<text>
  // so we never need to load every BOM into the browser.
  useEffect(() => {
    let cancelled = false;
    const handler = setTimeout(async () => {
      setBomsLoading(true);
      try {
        const params = { page_size: 100 };
        const term = (bomSearch || '').trim();
        if (term) params.search = term;
        const { items, total } = await bomAPI.getAllPaginated(params);
        if (cancelled) return;
        setBoms(items);
        setBomsTotal(total);
      } catch (e) {
        if (!cancelled) {
          setBoms([]);
          setBomsTotal(0);
        }
      } finally {
        if (!cancelled) setBomsLoading(false);
      }
    }, 250); // 250 ms debounce
    return () => { cancelled = true; clearTimeout(handler); };
  }, [bomSearch]);

  const selectBom = (bom) => {
    setSelectedBom(bom);
    const materials = (bom.items || []).map(item => ({
      seq: item.seq, item_id: item.item_id, item_description: item.item_description,
      quantity: (item.quantity || 0) * form.quantity, unit: item.unit,
      rate: item.rate, total_cost: item.total_cost ? item.total_cost * form.quantity : null,
      is_alternate: false, alternates: item.alternates || [],
    }));
    setForm(f => ({
      ...f, bom_id: bom.id, bom_number: bom.bom_number,
      product: bom.bom_name, fg_item_id: bom.fg_item_id,
      unit: bom.fg_uom || f.unit, materials,
    }));
    setMode('standalone');
  };

  const updateQuantity = (qty) => {
    if (!selectedBom) return;
    const q = Math.max(1, parseInt(qty) || 1);
    const materials = (selectedBom.items || []).map(item => ({
      seq: item.seq, item_id: item.item_id, item_description: item.item_description,
      quantity: (item.quantity || 0) * q, unit: item.unit,
      rate: item.rate, total_cost: item.total_cost ? item.total_cost * q : null,
      is_alternate: false, alternates: item.alternates || [],
    }));
    setForm(f => ({ ...f, quantity: q, materials }));
  };

  const swapMaterial = (seq, alt) => {
    setForm(f => ({
      ...f, materials: f.materials.map(m =>
        m.seq === seq ? { ...m, item_id: alt.item_id, item_description: alt.item_description, is_alternate: true, original_item_id: m.item_id } : m
      )
    }));
  };

  const handleSubmit = async () => {
    if (!form.product) return setError('Product is required');
    if (!form.bom_id) return setError('Please select a BOM');
    if (!form.branch) return setError('Please select a branch');
    if (!form.planned_end) return setError('Planned end date is required');
    try {
      setSubmitting(true);
      setError('');
      const submitData = { ...form, materials: form.materials.map(({ alternates, ...rest }) => rest) };
      await productionAPI.create(submitData);
      onBack();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create');
    } finally { setSubmitting(false); }
  };

  // BOM list is now fetched server-side — no client-side filtering needed.

  // BOM Selection view
  if (mode === 'select') {
    return (
      <div className="p-5 lg:p-6">
        <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-4" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <h1 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>New Production Order</h1>
        <p className="text-xs text-gray-500 mb-6">Select a BOM to start production</p>

        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input value={bomSearch} onChange={e => setBomSearch(e.target.value)} placeholder="Search BOMs by number, name, or FG item..." data-testid="bom-search"
            className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
          {bomsLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />}
        </div>

        {!bomsLoading && boms.length === 0 && (
          <p className="text-xs text-gray-500 italic text-center py-8" data-testid="bom-empty-state">
            {bomSearch.trim()
              ? `No BOM found matching "${bomSearch}". Try a different search term.`
              : 'No BOMs yet. Create one from the BOMs module first.'}
          </p>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {boms.map(bom => {
            const st = bom.status || 'Draft';
            const stClass = st === 'Active' ? 'bg-green-50 text-green-700 border border-green-200'
              : st === 'Draft' ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : 'bg-gray-50 text-gray-600 border border-gray-200';
            return (
              <div key={bom.id} onClick={() => selectBom(bom)} data-testid={`select-bom-${bom.id}`}
                className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-blue-200 cursor-pointer transition-all">
                <div className="flex items-center justify-between mb-2 gap-1">
                  <span className="text-[10px] font-mono font-semibold text-gray-400">{bom.bom_number}</span>
                  <div className="flex items-center gap-1">
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${stClass}`} title="BOM status">{st}</span>
                    <span className="px-2 py-0.5 text-[9px] font-semibold bg-blue-50 text-blue-700 rounded-full">{bom.fg_item_id}</span>
                  </div>
                </div>
                <h3 className="text-xs font-bold text-gray-900 mb-1 line-clamp-2">{bom.bom_name}</h3>
                <div className="flex items-center gap-3 text-[10px] text-gray-500">
                  <span>{bom.items?.length || 0} materials</span>
                  {bom.total_fg_cost > 0 && <span className="font-mono">Rs.{Number(bom.total_fg_cost).toLocaleString('en-IN')}</span>}
                </div>
              </div>
            );
          })}
        </div>
        {bomsTotal > boms.length && (
          <p className="text-[11px] text-gray-400 mt-4 text-center" data-testid="bom-more-hint">
            Showing {boms.length} of {bomsTotal} BOMs. Narrow your search to see specific ones.
          </p>
        )}
      </div>
    );
  }

  // Production form with BOM materials
  return (
    <div className="p-5 lg:p-6">
      <button onClick={() => { setMode('select'); setSelectedBom(null); }} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-4" data-testid="back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to BOM Selection
      </button>
      <h1 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>New Production Order</h1>
      <p className="text-xs text-gray-500 mb-5">Configure production details and materials</p>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{error}</div>}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left - Order Details */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-900">Order Details</h3>
            {selectedBom && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-[10px] text-gray-500">BOM</p>
                <p className="text-xs font-bold text-gray-900">{selectedBom.bom_number} - {selectedBom.bom_name}</p>
                <p className="text-[10px] text-gray-500 font-mono">{selectedBom.fg_item_id}</p>
              </div>
            )}
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Product Name</label>
              <input value={form.product} onChange={e => setForm({ ...form, product: e.target.value })} data-testid="prod-form-product"
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Quantity *</label>
                <input type="number" min={1} value={form.quantity} onChange={e => updateQuantity(e.target.value)} data-testid="prod-form-qty"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Unit</label>
                <input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Branch *</label>
              <select value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} data-testid="prod-form-branch"
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                <option value="">Select production branch</option>
                {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Start Date *</label>
                <input type="date" value={form.planned_start} onChange={e => setForm({ ...form, planned_start: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">End Date *</label>
                <input type="date" value={form.planned_end} onChange={e => setForm({ ...form, planned_end: e.target.value })} data-testid="prod-form-end"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
              </div>
            </div>
            {form.oc_reference && (
              <div className="p-2 bg-purple-50 rounded-lg text-[11px]">
                <span className="text-gray-500">OC Ref:</span> <span className="font-semibold">{form.oc_number}</span>
              </div>
            )}
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg resize-none" />
            </div>
            <button onClick={handleSubmit} disabled={submitting} data-testid="create-production-btn"
              className="w-full px-4 py-2.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: '#24549c' }}>
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Factory className="w-3.5 h-3.5" />}
              Create Production Order
            </button>
          </div>
        </div>

        {/* Right - BOM Materials Tree */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xs font-bold text-gray-900">Material Requirements (x{form.quantity})</h3>
              <span className="text-[10px] text-gray-500">{form.materials.length} items</span>
            </div>
            <div className="divide-y divide-gray-50 max-h-[500px] overflow-y-auto">
              {form.materials.map(mat => (
                <div key={mat.seq}>
                  <div className="flex items-center px-4 py-3 hover:bg-gray-50/50"
                    onClick={() => setExpandedItems(p => ({ ...p, [mat.seq]: !p[mat.seq] }))}>
                    <div className="w-6 flex-shrink-0">
                      {mat.alternates?.length > 0 ? (
                        expandedItems[mat.seq] ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                      ) : <div className="w-3.5" />}
                    </div>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-3 flex-shrink-0" style={{ backgroundColor: mat.is_alternate ? '#fef3c7' : '#24549c15' }}>
                      <span className="text-[9px] font-bold" style={{ color: mat.is_alternate ? '#b45309' : '#24549c' }}>#{mat.seq}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-mono text-gray-500">{mat.item_id}</span>
                        <span className="text-xs font-semibold text-gray-900 truncate">{mat.item_description}</span>
                        {mat.is_alternate && <span className="px-1.5 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-700 rounded">SWAPPED</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-[11px] flex-shrink-0">
                      <span className="text-gray-600 w-24 text-right">{Number(mat.quantity).toFixed(2)} {mat.unit}</span>
                      <span className="font-mono text-gray-600 w-20 text-right">{mat.rate != null ? `@${mat.rate}` : '-'}</span>
                      <span className="font-mono font-semibold text-gray-900 w-24 text-right">
                        {mat.total_cost != null ? `Rs.${Number(mat.total_cost).toLocaleString('en-IN')}` : '-'}
                      </span>
                    </div>
                  </div>
                  {expandedItems[mat.seq] && mat.alternates?.map(alt => (
                    <div key={alt.seq} className="flex items-center px-4 py-2 bg-gray-50/50 border-l-2 border-amber-200 ml-6 cursor-pointer hover:bg-amber-50/50"
                      onClick={() => swapMaterial(mat.seq, alt)}>
                      <ArrowRightLeft className="w-3 h-3 text-amber-500 mr-3" />
                      <span className="text-[10px] font-mono text-amber-600 mr-2">{alt.item_id}</span>
                      <span className="text-[11px] text-gray-600 flex-1">{alt.item_description}</span>
                      <button className="text-[9px] font-semibold text-amber-600 hover:text-amber-800 px-2 py-0.5 rounded bg-amber-50">Use This</button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionCreateForm;
